import React from 'react';
import { RupeeInput, NumberInput, RadioCards } from '../components/formFields';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Plus, Trash2 } from 'lucide-react';
import ComplianceNote from '../components/ComplianceNote';
import { fmtINR, scoreColor, toNum } from '../lib/format';
import { computeSipNeeded, computeLumpsumNeeded, computeSipAtRate, SCENARIOS, riskLabel, computeRiskScore } from '../lib/scoring';
import { GOAL_TYPES } from '../mock';

const Section = ({ title, sub, children }) => (
  <div>
    <div className="mb-5">
      <h2 className="text-2xl font-bold text-brand-indigo">{title}</h2>
      {sub && <p className="text-sm text-muted-foreground mt-1">{sub}</p>}
    </div>
    <div className="space-y-5">{children}</div>
  </div>
);

const KV = ({ label, value, tone = 'default' }) => {
  const c = tone === 'red' ? 'text-brand-red' : tone === 'green' ? 'text-emerald-600' : 'text-brand-indigo';
  return (
    <div className="flex items-baseline justify-between gap-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-sm font-semibold ${c}`}>{value}</div>
    </div>
  );
};

const LiveBox = ({ children }) => (
  <div className="bg-brand-bg border border-brand-border rounded-lg p-4 space-y-2">{children}</div>
);

export const Step1Profile = ({ s, set }) => (
  <Section title="Client Profile" sub="Basic demographic and tax context.">
    <div className="grid md:grid-cols-2 gap-5">
      <div className="md:col-span-2">
        <Label>Full name</Label>
        <Input value={s.name || ''} onChange={(e) => set('name', e.target.value)} className="mt-1 h-11" placeholder="Auto-filled from client record" />
      </div>
      <NumberInput id="age" label="Age (years)" value={s.age} onChange={(v) => set('age', v)} placeholder="41" />
      <NumberInput id="retire_age" label="Retirement target age" value={s.retire_age} onChange={(v) => set('retire_age', v)} placeholder="60" />
      <div>
        <Label>Dependents</Label>
        <div className="mt-1">
          <RadioCards columns={4} value={s.dependents} onChange={(v) => set('dependents', v)} options={[
            { value: '0', label: 'None' }, { value: '1', label: '1' }, { value: '2', label: '2' }, { value: '3+', label: '3+' }
          ]} />
        </div>
      </div>
      <div>
        <Label>City</Label>
        <Input value={s.city || ''} onChange={(e) => set('city', e.target.value)} className="mt-1 h-11" placeholder="Surat" />
      </div>
      <div className="md:col-span-2">
        <Label>Tax regime</Label>
        <Select value={s.regime} onValueChange={(v) => set('regime', v)}>
          <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Select regime" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Old">Old regime</SelectItem>
            <SelectItem value="New">New regime</SelectItem>
            <SelectItem value="Not sure">Not sure</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  </Section>
);

export const Step2NetWorth = ({ s, set }) => {
  const assets = toNum(s.mf_value) + toNum(s.equity_value) + toNum(s.fd_value) + toNum(s.realestate_value);
  const net = assets - toNum(s.total_loans);
  return (
    <Section title="Net Worth Statement" sub="Assets minus liabilities. All figures in Indian Rupees.">
      <div className="grid md:grid-cols-2 gap-5">
        <RupeeInput id="mf" label="Mutual fund investments" value={s.mf_value} onChange={(v) => set('mf_value', v)} />
        <RupeeInput id="eq" label="Stocks / Direct equity" value={s.equity_value} onChange={(v) => set('equity_value', v)} />
        <RupeeInput id="fd" label="Fixed deposits & savings" value={s.fd_value} onChange={(v) => set('fd_value', v)} />
        <RupeeInput id="re" label="Real estate value" value={s.realestate_value} onChange={(v) => set('realestate_value', v)} />
        <RupeeInput id="loans" label="Total outstanding loans" value={s.total_loans} onChange={(v) => set('total_loans', v)} hint="Home loan, personal loan, car loan etc." />
      </div>
      <LiveBox>
        <KV label="Total assets" value={fmtINR(assets, { compact: true })} />
        <KV label="Total liabilities" value={fmtINR(toNum(s.total_loans), { compact: true })} />
        <div className="border-t border-brand-border pt-2">
          <KV label="Net worth" value={fmtINR(net, { compact: true })} tone={net >= 0 ? 'green' : 'red'} />
        </div>
      </LiveBox>
    </Section>
  );
};

export const Step3Income = ({ s, set }) => {
  const surplus = toNum(s.monthly_income) - toNum(s.monthly_expense) - toNum(s.monthly_emi);
  const savingsRate = toNum(s.monthly_income) > 0 ? (surplus / toNum(s.monthly_income)) * 100 : 0;
  return (
    <Section title="Income & Expenses" sub="Monthly household cash flow.">
      <div className="grid md:grid-cols-2 gap-5">
        <RupeeInput id="inc" label="Monthly household income" value={s.monthly_income} onChange={(v) => set('monthly_income', v)} />
        <RupeeInput id="exp" label="Monthly living expenses" value={s.monthly_expense} onChange={(v) => set('monthly_expense', v)} />
        <RupeeInput id="emi" label="Monthly EMI outgo" value={s.monthly_emi} onChange={(v) => set('monthly_emi', v)} />
        <RupeeInput id="sip" label="Current monthly SIP" value={s.monthly_sip} onChange={(v) => set('monthly_sip', v)} />
      </div>
      <LiveBox>
        <KV label="Monthly surplus (Income − Expenses − EMI)" value={fmtINR(surplus, { compact: true })} tone={surplus > 0 ? 'green' : 'red'} />
        <KV label="Savings rate" value={`${savingsRate.toFixed(1)}%`} tone={savingsRate >= 20 ? 'green' : savingsRate >= 10 ? 'default' : 'red'} />
      </LiveBox>
    </Section>
  );
};

export const Step4Risk = ({ s, set }) => {
  const score = computeRiskScore(s);
  const label = riskLabel(score);
  return (
    <Section title="Risk Profile" sub="Assess client’s attitude to market risk.">
      <div className="space-y-6">
        <div>
          <Label className="text-sm font-semibold text-brand-indigo">If your portfolio dropped 20% in one month, you would:</Label>
          <div className="mt-2"><RadioCards value={s.risk_q1} onChange={(v) => set('risk_q1', v)} options={[
            { value: 'sell', label: 'Sell everything', sub: 'Preserve capital' },
            { value: 'wait', label: 'Wait and watch', sub: 'Stay put, review later' },
            { value: 'invest', label: 'Invest more', sub: 'Buy the dip' }
          ]} /></div>
        </div>
        <div>
          <Label className="text-sm font-semibold text-brand-indigo">Your primary investment goal is:</Label>
          <div className="mt-2"><RadioCards value={s.risk_q2} onChange={(v) => set('risk_q2', v)} options={[
            { value: 'capital', label: 'Capital protection' },
            { value: 'income', label: 'Steady income' },
            { value: 'wealth', label: 'Wealth creation' }
          ]} /></div>
        </div>
        <div>
          <Label className="text-sm font-semibold text-brand-indigo">Your investment horizon:</Label>
          <div className="mt-2"><RadioCards value={s.risk_q3} onChange={(v) => set('risk_q3', v)} options={[
            { value: '<3', label: 'Less than 3 years' },
            { value: '3-7', label: '3 to 7 years' },
            { value: '7plus', label: '7+ years' }
          ]} /></div>
        </div>
        <div>
          <Label className="text-sm font-semibold text-brand-indigo">Equity allocation comfort:</Label>
          <div className="mt-2"><RadioCards columns={4} value={s.risk_q4} onChange={(v) => set('risk_q4', v)} options={[
            { value: '<30', label: 'Below 30%' },
            { value: '30-60', label: '30 – 60%' },
            { value: '60-80', label: '60 – 80%' },
            { value: '80plus', label: 'Above 80%' }
          ]} /></div>
        </div>
      </div>
      <LiveBox>
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">Risk score</div>
          <div className={`text-lg font-bold ${scoreColor(score).text}`}>{score}<span className="text-xs text-muted-foreground"> / 100</span></div>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">Risk label</div>
          <div className="text-sm font-semibold text-brand-indigo">{label}</div>
        </div>
      </LiveBox>
    </Section>
  );
};

export const Step5Goals = ({ s, set }) => {
  const goals = s.goals || [];
  const scenarioShown = s.scenario_shown || 'both';
  const addGoal = () => set('goals', [...goals, { id: `g-${Date.now()}`, type: "Child's Education", amount: 1000000, years: 5, priority: 'Primary' }]);
  const updateGoal = (id, patch) => set('goals', goals.map((g) => (g.id === id ? { ...g, ...patch } : g)));
  const removeGoal = (id) => set('goals', goals.filter((g) => g.id !== id));

  const currentSip = toNum(s.monthly_sip);
  const illustrativeTotal = goals.reduce((a, g) => a + computeSipNeeded(g), 0);
  const gap = Math.max(0, illustrativeTotal - currentSip);

  // Filter which scenarios to display based on RM's audit choice.
  const shownScenarios = scenarioShown === 'both'
    ? SCENARIOS
    : SCENARIOS.filter((sc) => sc.key === scenarioShown);
  const gridCols = shownScenarios.length === 1 ? 'grid-cols-1' : 'grid-cols-2';

  return (
    <Section title="Goal Awareness Checklist" sub="Identify what the client is saving for. Add each goal separately.">
      <ComplianceNote>
        This checklist helps identify what the client is saving for. It is not a financial plan. For a comprehensive financial plan, clients should consult a SEBI Registered Investment Adviser.
      </ComplianceNote>

      <div className="border border-brand-border rounded-lg p-4 bg-white" data-testid="scenario-shown-picker">
        <Label className="text-sm font-semibold text-brand-indigo">Which scenario will you present to the client on the PDF?</Label>
        <div className="text-[11px] text-muted-foreground mb-3">Persisted with the session — audit-visible on the compliance trail.</div>
        <RadioCards columns={3} value={scenarioShown} onChange={(v) => set('scenario_shown', v)} options={[
          { value: 'both', label: 'Both side-by-side', sub: 'Conservative + Illustrative' },
          { value: 'conservative', label: 'Conservative only', sub: '@ 10% p.a.' },
          { value: 'illustrative', label: 'AMFI Illustrative only', sub: '@ 12.5% p.a.' }
        ]} />
      </div>

      <div className="space-y-3">
        {goals.map((g) => {
          return (
            <div key={g.id} className="border border-brand-border rounded-lg p-4 bg-white">
              <div className="grid md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-4">
                  <Label>Goal type</Label>
                  <Select value={g.type} onValueChange={(v) => updateGoal(g.id, { type: v })}>
                    <SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GOAL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-3">
                  <RupeeInput id={`amt-${g.id}`} label="Target amount" value={g.amount} onChange={(v) => updateGoal(g.id, { amount: v })} />
                </div>
                <div className="md:col-span-2">
                  <NumberInput id={`yrs-${g.id}`} label="Years" value={g.years} onChange={(v) => updateGoal(g.id, { years: v })} suffix="yrs" />
                </div>
                <div className="md:col-span-2">
                  <Label>Priority</Label>
                  <Select value={g.priority} onValueChange={(v) => updateGoal(g.id, { priority: v })}>
                    <SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Primary">Primary</SelectItem>
                      <SelectItem value="Secondary">Secondary</SelectItem>
                      <SelectItem value="Long-term">Long-term</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-1 flex justify-end">
                  <Button variant="ghost" size="icon" onClick={() => removeGoal(g.id)} className="text-brand-red hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className={`mt-3 grid ${gridCols} gap-2`} data-testid={`scenario-panel-${g.id}`}>
                {shownScenarios.map((sc) => {
                  const rateSip = computeSipAtRate(g, sc.rate);
                  const active = sc.key === 'illustrative';
                  const tone = sc.tone === 'green' ? 'border-emerald-300 bg-emerald-50/60' : sc.tone === 'amber' ? 'border-amber-300 bg-amber-50/60' : 'border-brand-indigo/40 bg-brand-bg';
                  const txt = sc.tone === 'green' ? 'text-emerald-700' : sc.tone === 'amber' ? 'text-amber-700' : 'text-brand-indigo';
                  return (
                    <div key={sc.key} className={`rounded-md border ${tone} px-3 py-2`} data-testid={`scenario-${sc.key}-${g.id}`}>
                      <div className="flex items-center justify-between">
                        <div className={`text-[10px] uppercase tracking-wider font-semibold ${txt}`}>{sc.label}</div>
                        {active && <span className="text-[9px] font-semibold text-brand-indigo/70">DEFAULT</span>}
                      </div>
                      <div className="text-[10px] text-muted-foreground">@{(sc.rate * 100).toFixed(1)}% p.a.</div>
                      <div className={`text-sm font-bold ${txt} mt-1`}>{fmtINR(rateSip)}<span className="text-[10px] font-normal text-muted-foreground">/mo</span></div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 text-[10px] text-muted-foreground">Lumpsum today (@12.5%): <span className="font-semibold text-brand-indigo">{fmtINR(computeLumpsumNeeded(g), { compact: true })}</span> · Illustrative only. Not guaranteed. See D-8.</div>
            </div>
          );
        })}
        <Button variant="outline" onClick={addGoal} className="border-dashed border-brand-indigo/40 text-brand-indigo"><Plus className="h-4 w-4 mr-2" /> Add goal</Button>
      </div>

      {goals.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-brand-border rounded-lg overflow-hidden">
            <thead className="bg-brand-bg">
              <tr className="text-left text-xs uppercase tracking-wider text-brand-indigo">
                <th className="px-3 py-2 font-semibold">Goal</th>
                <th className="px-3 py-2 font-semibold">Target</th>
                <th className="px-3 py-2 font-semibold">Years</th>
                <th className="px-3 py-2 font-semibold">SIP Needed/mo</th>
                <th className="px-3 py-2 font-semibold">Priority</th>
              </tr>
            </thead>
            <tbody>
              {goals.map((g) => (
                <tr key={g.id} className="border-t border-brand-border">
                  <td className="px-3 py-2 font-medium text-brand-indigo">{g.type}</td>
                  <td className="px-3 py-2">{fmtINR(g.amount, { compact: true })}</td>
                  <td className="px-3 py-2">{g.years}</td>
                  <td className="px-3 py-2 font-semibold">{fmtINR(computeSipNeeded(g))}</td>
                  <td className="px-3 py-2">{g.priority}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LiveBox>
        <div className={`grid ${gridCols} gap-3`} data-testid="goals-scenario-summary">
          {shownScenarios.map((sc) => {
            const total = goals.reduce((a, g) => a + computeSipAtRate(g, sc.rate), 0);
            const active = sc.key === 'illustrative';
            const tone = sc.tone === 'green' ? 'text-emerald-700 bg-emerald-50' : sc.tone === 'amber' ? 'text-amber-700 bg-amber-50' : 'text-brand-indigo bg-white';
            return (
              <div key={sc.key} className={`rounded-md p-3 border border-brand-border ${tone}`} data-testid={`goals-summary-${sc.key}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider font-semibold">{sc.label}</div>
                  {active && <span className="text-[9px] font-semibold opacity-70">DEFAULT</span>}
                </div>
                <div className="text-[10px] text-muted-foreground">@{(sc.rate * 100).toFixed(1)}% p.a.</div>
                <div className="text-base font-bold mt-1">{fmtINR(total)}<span className="text-[10px] font-normal text-muted-foreground">/mo</span></div>
              </div>
            );
          })}
        </div>
        <div className="border-t border-brand-border pt-2">
          <KV label="Current SIP allocated" value={`${fmtINR(currentSip)}/mo`} />
          <KV label="Illustrative gap (vs 12.5% total)" value={`${fmtINR(gap)}/mo`} tone={gap > 0 ? 'red' : 'green'} />
        </div>
      </LiveBox>
    </Section>
  );
};

export const Step6Insurance = ({ s, set }) => {
  const lcNeeded = toNum(s.monthly_income) * 12 * 10;
  const lcGap = Math.max(0, lcNeeded - toNum(s.life_cover));
  const hcGap = Math.max(0, 1000000 - toNum(s.health_cover));
  return (
    <Section title="Insurance Gap Awareness" sub="Life & health cover suitability check.">
      <div className="grid md:grid-cols-2 gap-5">
        <RupeeInput id="lc" label="Existing life insurance cover" value={s.life_cover} onChange={(v) => set('life_cover', v)} />
        <RupeeInput id="hc" label="Existing health cover (family floater)" value={s.health_cover} onChange={(v) => set('health_cover', v)} />
        <div className="flex items-center justify-between border border-brand-border rounded-lg p-4">
          <div>
            <div className="text-sm font-medium text-brand-indigo">Pure term plan in place?</div>
            <div className="text-[11px] text-muted-foreground">Cheap, high-cover income replacement</div>
          </div>
          <Switch checked={!!s.has_term} onCheckedChange={(v) => set('has_term', v)} />
        </div>
        <div className="flex items-center justify-between border border-brand-border rounded-lg p-4">
          <div>
            <div className="text-sm font-medium text-brand-indigo">Critical illness cover?</div>
            <div className="text-[11px] text-muted-foreground">Lump-sum on diagnosis of listed illnesses</div>
          </div>
          <Switch checked={!!s.has_critical} onCheckedChange={(v) => set('has_critical', v)} />
        </div>
      </div>
      <LiveBox>
        <KV label="10x income benchmark" value={fmtINR(lcNeeded, { compact: true })} />
        <KV label="Life cover gap" value={fmtINR(lcGap, { compact: true })} tone={lcGap > 0 ? 'red' : 'green'} />
        <KV label="Recommended health cover" value={fmtINR(1000000, { compact: true })} />
        <KV label="Health cover gap" value={fmtINR(hcGap, { compact: true })} tone={hcGap > 0 ? 'red' : 'green'} />
      </LiveBox>
      <ComplianceNote>
        Insurance gap analysis is based on the standard 10x annual income rule. This is for awareness only. Actual insurance need assessment must be done separately under IRDAI regulations.
      </ComplianceNote>
    </Section>
  );
};

export const Step7Retirement = ({ s, set }) => {
  const corpusNeeded = toNum(s.monthly_retire_need) * 12 * 25;
  const currentSavings = toNum(s.ppf_nps_epf) + toNum(s.other_retire_sav);
  const gap = Math.max(0, corpusNeeded - currentSavings);
  const yrs = Math.max(1, toNum(s.retire_age) - toNum(s.age));
  const monthlyR = 0.125 / 12;
  const n = yrs * 12;
  const sipNeeded = corpusNeeded > 0 ? (corpusNeeded - currentSavings * Math.pow(1.08, yrs)) * monthlyR / (Math.pow(1 + monthlyR, n) - 1) : 0;

  return (
    <Section title="Retirement Gap Awareness" sub="Indicative retirement corpus vs current savings trajectory.">
      <ComplianceNote>
        This is an indicative gap calculation based on client-provided data and standard thumb rules. It is not a personalised retirement plan.
      </ComplianceNote>
      <div className="grid md:grid-cols-3 gap-5">
        <RupeeInput id="mrn" label="Monthly income needed at retirement (today’s ₹)" value={s.monthly_retire_need} onChange={(v) => set('monthly_retire_need', v)} />
        <RupeeInput id="ppf" label="PPF / NPS / EPF accumulated" value={s.ppf_nps_epf} onChange={(v) => set('ppf_nps_epf', v)} />
        <RupeeInput id="other_ret" label="Other retirement savings" value={s.other_retire_sav} onChange={(v) => set('other_retire_sav', v)} />
      </div>
      <LiveBox>
        <KV label="Corpus required (25x annual spend)" value={fmtINR(corpusNeeded, { compact: true })} />
        <KV label="Current savings" value={fmtINR(currentSavings, { compact: true })} />
        <KV label="Gap" value={fmtINR(gap, { compact: true })} tone={gap > 0 ? 'red' : 'green'} />
        <KV label="Additional SIP needed (illustrative @12.5%)" value={`${fmtINR(Math.max(0, Math.round(sipNeeded)))}/mo`} />
        <div className="text-[10px] text-muted-foreground pt-2 border-t border-brand-border">
          25x rule is a standard thumb rule. Actual requirement depends on inflation, lifestyle, and other factors. Illustrative only. Not guaranteed.
        </div>
      </LiveBox>
    </Section>
  );
};

export const Step8Tax = ({ s, set }) => {
  const rem80c = Math.max(0, 150000 - toNum(s.sec_80c));
  const rem80d = Math.max(0, 25000 - toNum(s.sec_80d));
  return (
    <Section title="Tax Planning Awareness" sub="Deduction utilisation snapshot.">
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <Label>Tax regime</Label>
          <Select value={s.tax_regime} onValueChange={(v) => set('tax_regime', v)}>
            <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Select regime" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Old">Old regime</SelectItem>
              <SelectItem value="New">New regime</SelectItem>
              <SelectItem value="Not sure">Not sure</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Capital gains this year?</Label>
          <Select value={s.capital_gains} onValueChange={(v) => set('capital_gains', v)}>
            <SelectTrigger className="mt-1 h-11"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ltcg">LTCG</SelectItem>
              <SelectItem value="stcg">STCG</SelectItem>
              <SelectItem value="no">No</SelectItem>
              <SelectItem value="not_sure">Not sure</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <RupeeInput id="80c" label="80C investments this year" value={s.sec_80c} onChange={(v) => set('sec_80c', v)} hint="Limit ₹ 1,50,000" />
        <RupeeInput id="80d" label="80D health insurance premium" value={s.sec_80d} onChange={(v) => set('sec_80d', v)} hint="Family cover – typical limit ₹ 25,000" />
      </div>
      <LiveBox>
        <KV label="80C remaining" value={fmtINR(rem80c)} />
        <KV label="80D remaining" value={fmtINR(rem80d)} />
        <KV label="Estimated additional deduction available" value={fmtINR(rem80c + rem80d)} />
      </LiveBox>
      <ComplianceNote>
        Tax information is general and indicative. Clients must consult a qualified CA for personalised tax advice. Akta Wealth does not provide tax advisory services.
      </ComplianceNote>
    </Section>
  );
};

export const Step9Emergency = ({ s, set }) => {
  const outflow = toNum(s.monthly_expense) + toNum(s.monthly_emi);
  const efMonths = outflow > 0 ? toNum(s.liquid_savings) / outflow : 0;
  const benchmark = outflow * 6;
  const tone = efMonths >= 6 ? 'green' : efMonths >= 3 ? 'default' : 'red';

  return (
    <Section title="Emergency Fund & Portfolio Health" sub="Liquidity buffer and portfolio review cadence.">
      <div className="grid md:grid-cols-2 gap-5">
        <RupeeInput id="liq" label="Liquid savings easily accessible" value={s.liquid_savings} onChange={(v) => set('liquid_savings', v)} hint="Savings account, liquid fund, sweep FD" />
        <div>
          <Label>Current equity allocation</Label>
          <div className="mt-1"><RadioCards columns={4} value={s.equity_pct} onChange={(v) => set('equity_pct', v)} options={[
            { value: '<20', label: 'Below 20%' },
            { value: '20-40', label: '20 – 40%' },
            { value: '40-60', label: '40 – 60%' },
            { value: '60plus', label: 'Above 60%' }
          ]} /></div>
        </div>
        <div>
          <Label>Current debt / FD allocation</Label>
          <div className="mt-1"><RadioCards columns={4} value={s.debt_pct} onChange={(v) => set('debt_pct', v)} options={[
            { value: '<20', label: 'Below 20%' },
            { value: '20-40', label: '20 – 40%' },
            { value: '40-60', label: '40 – 60%' },
            { value: '60plus', label: 'Above 60%' }
          ]} /></div>
        </div>
        <div>
          <Label>Last portfolio review</Label>
          <div className="mt-1"><RadioCards columns={4} value={s.last_rebalance} onChange={(v) => set('last_rebalance', v)} options={[
            { value: '<6m', label: 'Under 6 months' },
            { value: '6-12m', label: '6 – 12 months' },
            { value: '1-2y', label: '1 – 2 years' },
            { value: 'never', label: 'Never' }
          ]} /></div>
        </div>
      </div>
      <LiveBox>
        <KV label="6-month benchmark" value={fmtINR(benchmark, { compact: true })} />
        <KV label="Current cover" value={`${efMonths.toFixed(1)} months`} tone={tone} />
      </LiveBox>

      <div className="space-y-3 pt-2">
        <Label>RM notes</Label>
        <Textarea value={s.rm_notes || ''} onChange={(e) => set('rm_notes', e.target.value)} placeholder="Key observations from this meeting…" className="min-h-[80px]" />
        <Label>Next action items</Label>
        <Textarea value={s.next_action || ''} onChange={(e) => set('next_action', e.target.value)} placeholder="Term plan quote, ELSS setup, rebalance review…" className="min-h-[70px]" />
        <div>
          <Label>Next follow-up date</Label>
          <Input type="date" value={s.follow_up_date || ''} onChange={(e) => set('follow_up_date', e.target.value)} className="mt-1 h-11 max-w-xs" />
        </div>
      </div>
    </Section>
  );
};
